import { useState, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Upload, Download, Database, Check, AlertTriangle, FileSpreadsheet, Loader2, Pencil } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import api from '@/lib/api';
import { toast } from 'sonner';
import SWMUnifiedImportWizard from './SWMUnifiedImportWizard';
import EditableRegister from '@/components/register/EditableRegister';

const IMPORT_TYPES = [
  { id: 'STRUCTURE_REGISTER', label: 'Structure Register', description: 'Import structures (pipe racks, platforms, etc.)', registerId: 'swm.structures' },
  { id: 'STRUCTURAL_MTO', label: 'Structural MTO (Tags)', description: 'Import material take-off tags', registerId: 'swm.mto' },
  { id: 'DRAWING_REGISTER', label: 'Drawing Register', description: 'Import drawing status register', registerId: 'swm.drawings' },
  { id: 'PO_DETAILS', label: 'PO Details', description: 'Import purchase order details', registerId: 'swm.po-details' },
  { id: 'STOCK_INVENTORY', label: 'Stock Inventory', description: 'Import site stock/inventory data', registerId: 'swm.stock' },
  { id: 'MARK_NUMBER_REGISTER', label: 'Mark Number Register', description: 'Import fabrication mark numbers', registerId: 'swm.marks' },
  { id: 'TAG_MARK_MAPPING', label: 'Tag-Mark Mapping', description: 'Import tag-to-mark number mappings', registerId: 'swm.tag-mark' },
  { id: 'GRN_UPDATE', label: 'GRN Update', description: 'Update goods received notes against POs', registerId: 'swm.grn' },
  { id: 'FAB_PROGRESS_UPDATE', label: 'Fab Progress Update', description: 'Update fabrication stage gate dates', registerId: 'swm.fab-progress' },
];

export default function SWMDataImport({ projectId }) {
  const { isDark } = useTheme();
  const [seeding, setSeeding] = useState(false);
  const [importResults, setImportResults] = useState({});
  const [uploading, setUploading] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [editRegister, setEditRegister] = useState(null);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/seed-all`);
      const counts = res.data?.counts || {};
      const summary = Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(', ');
      toast.success(`Seeded SWM data${summary ? `: ${summary}` : ''}`);
    } catch (err) {
      toast.error('Failed to seed data: ' + (err.response?.data?.detail || err.message));
    }
    setSeeding(false);
  };

  const handleTemplateDownload = async (importType) => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/templates/${importType}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SWM_${importType}_Template.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Template downloaded: ${importType}`);
    } catch (err) {
      toast.error('Download failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleExport = async (exportType) => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/export/${exportType}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SWM_${exportType}_Export.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported: ${exportType}`);
    } catch (err) {
      toast.error('Export failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleFileUpload = async (importType, file) => {
    setUploading(prev => ({ ...prev, [importType]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/v1/projects/${projectId}/swm/import/${importType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResults(prev => ({ ...prev, [importType]: res.data }));
      if (res.data.status === 'COMPLETED') {
        toast.success(`Imported ${res.data.imported}/${res.data.total} records for ${importType}`);
      } else {
        toast.error(`Import ${res.data.status}: ${res.data.errors?.[0] || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(prev => ({ ...prev, [importType]: false }));
  };

  const exportTypes = ['STRUCTURE_REGISTER', 'STRUCTURAL_MTO', 'DRAWING_REGISTER', 'PO_DETAILS', 'STOCK_INVENTORY', 'MARK_NUMBER_REGISTER', 'TAG_MARK_MAPPING'];

  return (
    <div className="space-y-4" data-testid="swm-data-import">
      {/* Unified Import Wizard */}
      <SWMUnifiedImportWizard projectId={projectId} onImportComplete={() => setRefreshKey(k => k + 1)} />

      {/* Seed & Export Row */}
      <div className="flex items-center gap-3 flex-wrap p-3 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <button onClick={handleSeed} disabled={seeding} data-testid="swm-seed-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-white"
          style={{ background: seeding ? '#94A3B8' : '#F59206' }}>
          {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
          {seeding ? 'Seeding...' : 'Seed Demo Data'}
        </button>
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>|</span>
        <span className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Export:</span>
        {exportTypes.map(et => (
          <button key={et} onClick={() => handleExport(et)} data-testid={`swm-export-${et}`}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded border transition-colors"
            style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
            <Download className="w-3 h-3" />
            {et.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace('Register', 'Reg').replace('Number', '#').replace('Inventory', 'Inv').replace('Details', 'Det')}
          </button>
        ))}
      </div>

      {/* Import Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {IMPORT_TYPES.map(imp => {
          const result = importResults[imp.id];
          const isUploading = uploading[imp.id];
          return (
            <div key={imp.id} className="p-3 rounded-lg" data-testid={`swm-import-card-${imp.id}`}
              style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{imp.label}</span>
                <button onClick={() => handleTemplateDownload(imp.id)} data-testid={`swm-template-${imp.id}`}
                  className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded"
                  style={{ color: '#3B82F6', background: '#3B82F610' }}>
                  <FileSpreadsheet className="w-3 h-3" /> Template
                </button>
              </div>
              <p className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>{imp.description}</p>
              <label className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg cursor-pointer text-xs font-medium transition-colors"
                data-testid={`swm-upload-${imp.id}`}
                style={{ background: isUploading ? '#94A3B820' : '#F5920610', color: isUploading ? '#94A3B8' : '#F59206', border: '1px dashed', borderColor: isUploading ? '#94A3B8' : '#F59206' }}>
                {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {isUploading ? 'Uploading...' : 'Upload Excel'}
                <input type="file" accept=".xlsx,.xls" className="hidden" disabled={isUploading}
                  onChange={e => { if (e.target.files[0]) handleFileUpload(imp.id, e.target.files[0]); e.target.value = ''; }} />
              </label>
              {imp.registerId && (
                <button onClick={() => setEditRegister(imp.registerId)} data-testid={`swm-edit-${imp.id}`}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors text-white"
                  style={{ background: '#143A6B' }}>
                  <Pencil className="w-3 h-3" /> Edit Rows In-Platform
                </button>
              )}
              {result && (
                <div className="mt-2 p-2 rounded text-[10px]" data-testid={`swm-import-result-${imp.id}`}
                  style={{ background: result.status === 'COMPLETED' ? '#10B98110' : '#EF444410', color: result.status === 'COMPLETED' ? '#10B981' : '#EF4444' }}>
                  {result.status === 'COMPLETED' ? <Check className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
                  {result.status}: {result.imported}/{result.total} records
                  {result.errors?.length > 0 && (
                    <div className="mt-1 max-h-16 overflow-y-auto">
                      {result.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Editable Register Sheet */}
      <Sheet open={!!editRegister} onOpenChange={o => !o && setEditRegister(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[92vw] p-0 flex flex-col">
          <SheetHeader className="sr-only">
            <SheetTitle>Edit Register Rows</SheetTitle>
          </SheetHeader>
          {editRegister && <EditableRegister projectId={projectId} registerId={editRegister} onClose={() => setEditRegister(null)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
